import { useEffect, useState } from "react";
import { showNotification } from "@mantine/notifications";
import { normalizeErrorString } from "@medplum/core";
import { useMedplum } from "@medplum/react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Group,
  TextInput,
  Select,
  Paper,
  Text,
  Modal,
  Title,
  createStyles,
  PasswordInput,
} from "@mantine/core";
import ClientTabs from "./Tabs";
import { getProjectId } from "../../utils";
import { getExtensionValues } from "../HomePage.utils";
import ConfirmModal from "../../components/CustomModal";
import { EHR, Wearable } from "../../constants";

const useStyles = createStyles((theme) => ({
  createTitle: {
    fontSize: "30px",
    fontWeight: "bold",
  },
}));

function EditClientDetails(): JSX.Element {
  const medplum = useMedplum();
  const navigate = useNavigate();
  const projectId = getProjectId(medplum);
  const { classes } = useStyles();
  const { clientId, resourceType }: any = useParams();
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [clientDetails, setClientDetails] = useState<any>({});

  useEffect(() => {
    if (clientId && resourceType) {
      getClientDetails();
    }
  }, []);

  const getClientDetails = () => {
    medplum.readResource(resourceType, `${clientId}`).then((res: any) => {
      const additionalFields = getExtensionValues(res.meta, "additionalFields");
      const updatedData = { ...res, additionalData: { ...additionalFields } };
      setClientDetails(updatedData);
    });
  };

  const createClient = async () => {
    const body = {
      name: clientDetails?.name,
      description: clientDetails?.description,
      redirectUri: clientDetails?.redirectUri,
      accessPolicy: clientDetails?.accessPolicy,
    };

    try {
      const createdClient = await medplum.post(
        "admin/projects/" + projectId + "/client",
        body
      );
      medplum.invalidateSearches("ClientApplication");
      medplum.invalidateSearches("ProjectMembership");
      return createdClient;
    } catch (err) {
      showNotification({
        color: "red",
        message: normalizeErrorString(err),
        autoClose: true,
      });
    }
  };

  const onSubmit = async (event: any) => {
    event?.preventDefault();
    const requiredFields = [
      "scope",
      "clientType",
      "integrationType",
      "environment",
    ];

    if (clientDetails.additionalData.clientType === "EHR") {
      requiredFields.push("launchType");
    }

    const missingFields = requiredFields.filter(
      (field) => !clientDetails.additionalData?.[field]
    );
    if (missingFields.length > 0) {
      showNotification({
        color: "red",
        message: `Please fill in the required fields: ${missingFields.join(
          ", "
        )}`,
        autoClose: true,
      });
      return;
    }

    const clientExtension = [
      {
        url: "additionalFields",
        extension: requiredFields.map((field) => ({
          url: field,
          valueString: clientDetails.additionalData[field],
        })),
      },
    ];

    let clientData;

    if (!clientId) {
      clientData = await createClient();
    } else {
      clientData = clientDetails;
    }

    clientData.meta.extension = clientExtension;
    clientData.identityProvider = clientDetails.identityProvider;
    if (clientData.additionalData) {
      clientData.additionalData = undefined;
    }

    medplum
      .updateResource(clientData)
      .then((res) => {
        setClientDetails({});
        showNotification({
          color: "green",
          message: "Success",
          autoClose: true,
        });
        if (clientId) {
          navigate(`/client/details/${resourceType}/${clientId}`);
        } else {
          navigate(`/client/details/${res.resourceType}/${res.id}`);
        }
      })
      .catch((err) => {
        showNotification({
          color: "red",
          message: normalizeErrorString(err),
          autoClose: true,
        });
      });
  };

  const deleteClient = () => {
    medplum
      .deleteResource(resourceType, `${clientId}`)
      .then((res) => {
        showNotification({
          color: "green",
          message: "Deleted successfully",
          autoClose: true,
        });
        navigate("/admin/integration");
      })
      .catch((err) => {
        showNotification({
          color: "red",
          message: normalizeErrorString(err),
          autoClose: true,
        });
      });
  };

  return (
    <>
      <ConfirmModal
        isOpen={showDeleteConfirmModal}
        onClose={() => {
          setShowDeleteConfirmModal(false);
        }}
        title="Delete Client"
        message="Are you sure you want to delete this ClientApplication?"
        onCancel={() => setShowDeleteConfirmModal(false)}
        onConfirm={deleteClient}
        Button1="No"
        Button2="Delete"
      />

      {clientId && resourceType ? (
        <ClientTabs currentTab={"Edit"} clientName={clientDetails?.name} />
      ) : (
        ""
      )}
      <Paper
        sx={{
          marginTop: "20px",
          borderRadius: "8px",
          marginLeft: "40px",
          marginRight: "40px",
          padding: "20px",
          background: "'#FFF",
          boxShadow: "0px 1px 24px 2px rgba(0, 0, 0, 0.06)",
        }}
      >
        <Title className={classes.createTitle}>
          {clientId ? "Edit Client" : "Create new Client"}
        </Title>
        <form onSubmit={onSubmit}>
          {clientId && (
            <TextInput
              label="Client Id"
              placeholder="Client Id"
              onChange={(e) => {
                setClientDetails({
                  ...clientDetails,
                  id: e.target.value,
                });
              }}
              disabled={true}
              mt="lg"
              radius="md"
              size="lg"
              value={clientDetails?.id}
            />
          )}
          <TextInput
            label="Application Name"
            placeholder="Application Name"
            onChange={(e) => {
              setClientDetails({
                ...clientDetails,
                name: e.target.value,
              });
            }}
            required={true}
            mt="lg"
            size="lg"
            radius="md"
            value={clientDetails?.name}
          />
          <TextInput
            label="Description"
            placeholder="Description"
            onChange={(e) => {
              setClientDetails({
                ...clientDetails,
                description: e.target.value,
              });
            }}
            mt="lg"
            size="lg"
            radius="md"
            value={clientDetails?.description}
          />
          {/* <TextInput
            label="Access Policy"
            placeholder="Access Policy"
            onChange={(e) => {
              setClientDetails({
                ...clientDetails,
                accessPolicy: e.target.value,
              });
            }}
            mt="lg"
            size="lg"
            radius="md"
            value={clientDetails?.accessPolicy}
          /> */}
          <TextInput
            label="Scope"
            placeholder="Scope"
            required={true}
            onChange={(e) => {
              setClientDetails({
                ...clientDetails,
                additionalData: {
                  ...clientDetails?.additionalData,
                  scope: e.target.value,
                },
              });
            }}
            mt="lg"
            size="lg"
            radius="md"
            value={clientDetails?.additionalData?.scope}
          />
          <Select
            label="Client Type"
            placeholder="Client Type"
            required={true}
            data={["EHR", "Wearable"]}
            mt="lg"
            size="lg"
            radius="md"
            disabled
            value={clientDetails?.additionalData?.clientType}
            onChange={(val) => {
              setClientDetails({
                ...clientDetails,
                additionalData: {
                  ...clientDetails?.additionalData,
                  clientType: val,
                },
              });
            }}
          />
          <Select
            label="Integration Type"
            placeholder="Integration Type"
            required={true}
            data={
              clientDetails?.additionalData?.clientType === "EHR"
                ? EHR
                : Wearable
            }
            mt="lg"
            size="lg"
            radius="md"
            value={clientDetails?.additionalData?.integrationType}
            onChange={(val) => {
              setClientDetails({
                ...clientDetails,
                additionalData: {
                  ...clientDetails?.additionalData,
                  integrationType: val,
                },
              });
            }}
          />
          {clientDetails?.additionalData?.clientType === "EHR" && (
            <Select
              label="Launch Type"
              placeholder="Select Launch Type"
              required={true}
              data={[
                "Patient Standalone",
                "Practitioner Standalone",
                "Backend System",
              ]}
              mt="lg"
              size="lg"
              radius="md"
              value={clientDetails?.additionalData?.launchType}
              onChange={(val) => {
                setClientDetails({
                  ...clientDetails,
                  additionalData: {
                    ...clientDetails?.additionalData,
                    launchType: val,
                  },
                });
              }}
            />
          )}
          <Select
            label="Credentials"
            placeholder="Select Credentials"
            required={true}
            data={["Sandbox", "Production"]}
            mt="lg"
            size="lg"
            radius="md"
            value={clientDetails?.additionalData?.environment}
            onChange={(val) => {
              setClientDetails({
                ...clientDetails,
                additionalData: {
                  ...clientDetails?.additionalData,
                  environment: val,
                },
              });
            }}
          />
          <TextInput
            label="JWT secret token"
            placeholder="JWT secret token"
            onChange={(e) => {
              setClientDetails({
                ...clientDetails,
                jwksUri: e.target.value,
              });
            }}
            mt="lg"
            size="lg"
            radius="md"
            value={clientDetails?.jwksUri}
          />
          <TextInput
            label="Redirect URL"
            placeholder="Redirect URL"
            onChange={(e) => {
              setClientDetails({
                ...clientDetails,
                redirectUri: e.target.value,
              });
            }}
            required={true}
            mt="lg"
            size="lg"
            radius="md"
            value={clientDetails?.redirectUri}
          />
          <TextInput
            label="Auth URL"
            placeholder="Auth URL"
            value={clientDetails?.identityProvider?.authorizeUrl}
            required={true}
            mt="lg"
            size="lg"
            radius="md"
            onChange={(e) => {
              setClientDetails({
                ...clientDetails,
                identityProvider: {
                  ...clientDetails?.identityProvider,
                  authorizeUrl: e.target.value,
                },
              });
            }}
          />
          <TextInput
            label="User Info URL"
            placeholder="User Info URL"
            value={clientDetails?.identityProvider?.userInfoUrl}
            required={true}
            mt="lg"
            size="lg"
            radius="md"
            onChange={(e) => {
              setClientDetails({
                ...clientDetails,
                identityProvider: {
                  ...clientDetails?.identityProvider,
                  userInfoUrl: e.target.value,
                },
              });
            }}
          />
          <TextInput
            label="Token URL"
            placeholder="Token URL"
            value={clientDetails?.identityProvider?.tokenUrl}
            required={true}
            mt="lg"
            size="lg"
            radius="md"
            onChange={(e) => {
              setClientDetails({
                ...clientDetails,
                identityProvider: {
                  ...clientDetails?.identityProvider,
                  tokenUrl: e.target.value,
                },
              });
            }}
          />
          <PasswordInput
            label="Client ID"
            placeholder="Client ID"
            value={clientDetails?.identityProvider?.clientId}
            required={true}
            mt="lg"
            size="lg"
            radius="md"
            onChange={(e) => {
              setClientDetails({
                ...clientDetails,
                identityProvider: {
                  ...clientDetails?.identityProvider,
                  clientId: e.target.value,
                },
              });
            }}
          />
          <PasswordInput
            label="Client Secret"
            placeholder="Client Secret"
            value={clientDetails?.identityProvider?.clientSecret}
            required={true}
            mt="lg"
            size="lg"
            radius="md"
            onChange={(e) => {
              setClientDetails({
                ...clientDetails,
                identityProvider: {
                  ...clientDetails?.identityProvider,
                  clientSecret: e.target.value,
                },
              });
            }}
          />
          <Group position="right" mt="lg">
            <Button type="submit" size="lg">
              Save
            </Button>
            <Button
              type="button"
              variant="outline"
              color="red"
              size="lg"
              onClick={() => setShowDeleteConfirmModal(true)}
            >
              Delete
            </Button>
          </Group>
        </form>
      </Paper>
    </>
  );
}

export default EditClientDetails;
